import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Edit, Trash2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Helmet } from "react-helmet-async";

interface Blog {
  id: string;
  title: string;
  slug: string;
  short_description: string;
  content: string;
  image_url?: string;
  author_name: string;
  is_published: boolean;
  is_featured: boolean;
  seo_title?: string;
  seo_description?: string;
  seo_keywords?: string;
  reading_time_minutes: number;
  category_id?: string;
  created_at: string;
  updated_at: string;
  blog_categories?: {
    name: string;
    color: string;
  };
}

interface BlogCategory {
  id: string;
  name: string;
  slug: string;
  color: string;
}

interface BlogFormData {
  title: string;
  slug: string;
  short_description: string;
  content: string;
  image_url: string;
  author_name: string;
  is_published: boolean;
  is_featured: boolean;
  seo_title: string;
  seo_description: string;
  seo_keywords: string;
  reading_time_minutes: number;
  category_id: string;
}

const BlogManager = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBlog, setEditingBlog] = useState<Blog | null>(null);
  const [formData, setFormData] = useState<BlogFormData>({
    title: "",
    slug: "",
    short_description: "",
    content: "",
    image_url: "",
    author_name: "TinyMinds Team",
    is_published: false,
    is_featured: false,
    seo_title: "",
    seo_description: "",
    seo_keywords: "",
    reading_time_minutes: 5,
    category_id: "",
  });

  const queryClient = useQueryClient();

  // Fetch blogs
  const { data: blogs, isLoading } = useQuery({
    queryKey: ["admin-blogs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blogs")
        .select(`
          *,
          blog_categories (
            name,
            color
          )
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Blog[];
    },
  });

  // Fetch categories
  const { data: categories } = useQuery({
    queryKey: ["blog-categories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_categories")
        .select("*")
        .eq("is_active", true)
        .order("display_order", { ascending: true });

      if (error) throw error;
      return data as BlogCategory[];
    },
  });

  // Create/Update blog mutation
  const saveBlogMutation = useMutation({
    mutationFn: async (data: any) => {
      if (editingBlog) {
        const { error } = await supabase
          .from("blogs")
          .update(data)
          .eq("id", editingBlog.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("blogs")
          .insert([data]);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-blogs"] });
      toast.success(editingBlog ? "Blog updated successfully!" : "Blog created successfully!");
      handleCloseDialog();
    },
    onError: (error) => {
      console.error("Error saving blog:", error);
      toast.error("Failed to save blog");
    },
  });

  // Delete blog mutation
  const deleteBlogMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("blogs")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-blogs"] });
      toast.success("Blog deleted successfully!");
    },
    onError: (error) => {
      console.error("Error deleting blog:", error);
      toast.error("Failed to delete blog");
    },
  });

  const handleOpenDialog = (blog?: Blog) => {
    if (blog) {
      setEditingBlog(blog);
      setFormData({
        title: blog.title,
        slug: blog.slug,
        short_description: blog.short_description,
        content: blog.content,
        image_url: blog.image_url || "",
        author_name: blog.author_name,
        is_published: blog.is_published,
        is_featured: blog.is_featured,
        seo_title: blog.seo_title || "",
        seo_description: blog.seo_description || "",
        seo_keywords: blog.seo_keywords || "",
        reading_time_minutes: blog.reading_time_minutes,
        category_id: blog.category_id || "",
      });
    } else {
      setEditingBlog(null);
      setFormData({
        title: "",
        slug: "",
        short_description: "",
        content: "",
        image_url: "",
        author_name: "TinyMinds Team",
        is_published: false,
        is_featured: false,
        seo_title: "",
        seo_description: "",
        seo_keywords: "",
        reading_time_minutes: 5,
        category_id: "",
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingBlog(null);
  };

  const handleInputChange = (field: keyof BlogFormData, value: string | number | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-generate slug from title
    if (field === "title" && typeof value === "string") {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .trim();
      setFormData(prev => ({ ...prev, slug }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title || !formData.slug || !formData.short_description || !formData.content) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Clean up the form data - convert empty strings to null for optional fields
    const cleanedData = {
      ...formData,
      category_id: formData.category_id || null,
      image_url: formData.image_url || null,
      seo_title: formData.seo_title || null,
      seo_description: formData.seo_description || null,
      seo_keywords: formData.seo_keywords || null,
    };

    saveBlogMutation.mutate(cleanedData);
  };

  const handleDelete = (id: string) => {
    if (window.confirm("Are you sure you want to delete this blog?")) {
      deleteBlogMutation.mutate(id);
    }
  };

  if (isLoading) {
    return <div className="flex justify-center p-8">Loading blogs...</div>;
  }

  return (
    <div className="space-y-6">
       <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold">Blog Management</h2>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => handleOpenDialog()} className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Add New Blog
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingBlog ? "Edit Blog" : "Create New Blog"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => handleInputChange("title", e.target.value)}
                    placeholder="Enter blog title"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="slug">Slug *</Label>
                  <Input
                    id="slug"
                    value={formData.slug}
                    onChange={(e) => handleInputChange("slug", e.target.value)}
                    placeholder="blog-url-slug"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="short_description">Short Description *</Label>
                <Textarea
                  id="short_description"
                  value={formData.short_description}
                  onChange={(e) => handleInputChange("short_description", e.target.value)}
                  placeholder="Brief description of the blog post"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="content">Content *</Label>
                <RichTextEditor
                  value={formData.content}
                  onChange={(value) => handleInputChange("content", value)}
                  placeholder="Full blog content"
                  height="300px"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="image_url">Featured Image URL</Label>
                  <Input
                    id="image_url"
                    value={formData.image_url}
                    onChange={(e) => handleInputChange("image_url", e.target.value)}
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="author_name">Author Name</Label>
                  <Input
                    id="author_name"
                    value={formData.author_name}
                    onChange={(e) => handleInputChange("author_name", e.target.value)}
                    placeholder="Author name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category_id">Category</Label>
                  <Select value={formData.category_id} onValueChange={(value) => handleInputChange("category_id", value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories?.map((category) => (
                        <SelectItem key={category.id} value={category.id}>
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reading_time">Reading Time (minutes)</Label>
                  <Input
                    id="reading_time"
                    type="number"
                    min="1"
                    value={formData.reading_time_minutes}
                    onChange={(e) => handleInputChange("reading_time_minutes", parseInt(e.target.value))}
                  />
                </div>
              </div>

              {/* SEO Fields */}
              <div className="space-y-4 border-t pt-4">
                <h4 className="font-semibold">SEO Settings</h4>
                <div className="space-y-2">
                  <Label htmlFor="seo_title">SEO Title</Label>
                  <Input
                    id="seo_title"
                    value={formData.seo_title}
                    onChange={(e) => handleInputChange("seo_title", e.target.value)}
                    placeholder="SEO optimized title"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="seo_description">SEO Description</Label>
                  <Textarea
                    id="seo_description"
                    value={formData.seo_description}
                    onChange={(e) => handleInputChange("seo_description", e.target.value)}
                    placeholder="SEO meta description"
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="seo_keywords">SEO Keywords</Label>
                  <Input
                    id="seo_keywords"
                    value={formData.seo_keywords}
                    onChange={(e) => handleInputChange("seo_keywords", e.target.value)}
                    placeholder="keyword1, keyword2, keyword3"
                  />
                </div>
              </div>

              {/* Publishing Options */}
              <div className="flex items-center justify-between space-x-4 border-t pt-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_published"
                    checked={formData.is_published}
                    onCheckedChange={(checked) => handleInputChange("is_published", checked)}
                  />
                  <Label htmlFor="is_published">Published</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_featured"
                    checked={formData.is_featured}
                    onCheckedChange={(checked) => handleInputChange("is_featured", checked)}
                  />
                  <Label htmlFor="is_featured">Featured</Label>
                </div>
              </div>

              <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={handleCloseDialog}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saveBlogMutation.isPending}>
                  {saveBlogMutation.isPending ? "Saving..." : editingBlog ? "Update Blog" : "Create Blog"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-6">
        {blogs?.map((blog) => (
          <Card key={blog.id}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    {blog.title}
                    {blog.is_published ? (
                      <Badge variant="default" className="bg-green-100 text-green-800">
                        <Eye className="w-3 h-3 mr-1" />
                        Published
                      </Badge>
                    ) : (
                      <Badge variant="secondary">
                        <EyeOff className="w-3 h-3 mr-1" />
                        Draft
                      </Badge>
                    )}
                    {blog.is_featured && (
                      <Badge variant="default" className="bg-yellow-100 text-yellow-800">
                        Featured
                      </Badge>
                    )}
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    By {blog.author_name} • {format(new Date(blog.created_at), "MMM d, yyyy")}
                  </p>
                  {blog.blog_categories && (
                    <Badge className={`mt-2 bg-gradient-to-r ${blog.blog_categories.color} text-white border-0`}>
                      {blog.blog_categories.name}
                    </Badge>
                  )}
                </div>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenDialog(blog)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(blog.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-2">{blog.short_description}</p>
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span>{blog.reading_time_minutes} min read</span>
                <span>Slug: {blog.slug}</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {(!blogs || blogs.length === 0) && (
        <Card>
          <CardContent className="text-center py-8">
            <p className="text-muted-foreground">No blogs found. Create your first blog to get started!</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BlogManager;